import { useState, useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import { X } from 'lucide-react';
import VoiceInput from '@/components/shared/VoiceInput';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import * as api from '@/services/api';

export default function ClientForm({ client, onClose }) {
  const { loadClients, switchClient, addToast } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: client?.name || '',
    domain: client?.domain || '',
    brand_color: client?.brand_color || '#1A3A5C',
    scan_freq: client?.scan_freq || 'weekly',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleVoiceResult = (text, field) => {
    if (field === 'domain') {
      const cleaned = text.toLowerCase().replace(/\s+/g, '').replace(/^(https?:\/\/)?(www\.)?/, '');
      setFormData(prev => ({ ...prev, domain: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [field]: text }));
    }
  };

  const handleDomainBlur = () => {
    let domain = formData.domain.toLowerCase().trim();
    domain = domain.replace(/^(https?:\/\/)?(www\.)?/, '');
    setFormData(prev => ({ ...prev, domain }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Client name is required';
    if (!formData.domain.trim()) newErrors.domain = 'Domain is required';
    else if (!/^[a-z0-9]+([\-\.][a-z0-9]+)*\.[a-z]{2,}$/i.test(formData.domain)) {
      newErrors.domain = 'Enter a valid domain (e.g., example.com)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsLoading(true);
    
    try {
      let response;
      if (client) {
        response = await api.updateClient(client.id, formData);
      } else {
        response = await api.createClient(formData);
      }

      if (response.error) {
        addToast(`Error saving client: ${response.error}`, 'error');
      } else {
        addToast(`Client ${client ? 'updated' : 'added'} successfully`, 'success');
        await loadClients();
        if (response.data?.id) {
          switchClient(response.data.id);
        }
        onClose();
      }
    } catch (err) {
      addToast('An unexpected error occurred.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={(e) => e.preventDefault()} data-testid="client-form">
      {/* Client Name */}
      <div>
        <label className="block text-sm font-mono text-[#94A3B8] mb-1">Client Name</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="input-field flex-1"
            placeholder="e.g., River Bank & Trust"
            data-testid="client-name-input"
          />
          <VoiceInput onResult={(text) => handleVoiceResult(text, 'name')} />
        </div>
        {errors.name && <p className="text-xs text-[#EF4444] mt-1">{errors.name}</p>}
      </div>

      {/* Domain */}
      <div>
        <label className="block text-sm font-mono text-[#94A3B8] mb-1">Domain</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            name="domain"
            value={formData.domain}
            onChange={handleInputChange}
            onBlur={handleDomainBlur}
            className="input-field flex-1"
            placeholder="e.g., riverbankandtrust.com"
            data-testid="client-domain-input"
          />
          <VoiceInput onResult={(text) => handleVoiceResult(text, 'domain')} />
        </div>
        {errors.domain && <p className="text-xs text-[#EF4444] mt-1">{errors.domain}</p>}
      </div>

      {/* Scan Frequency */}
      <div>
        <label className="block text-sm font-mono text-[#94A3B8] mb-1">Scan Frequency</label>
        <select
          name="scan_freq"
          value={formData.scan_freq}
          onChange={handleInputChange}
          className="select"
          data-testid="client-freq-select"
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Brand Color */}
      <div>
        <label className="block text-sm font-mono text-[#94A3B8] mb-1">Brand Color</label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            name="brand_color"
            value={formData.brand_color}
            onChange={handleInputChange}
            className="w-12 h-10 rounded-lg border border-[#1E2D45] cursor-pointer"
            data-testid="client-color-input"
          />
          <span className="text-sm font-mono text-[#4A5568]">{formData.brand_color}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-[#1E2D45]">
        <button 
          type="button" 
          onClick={onClose} 
          className="button-ghost"
          data-testid="client-form-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="button-primary flex items-center gap-2"
          data-testid="client-form-submit"
        >
          {isLoading ? <LoadingSpinner size="sm" /> : null}
          {client ? 'Save Changes' : 'Add Client'}
        </button>
      </div>
    </form>
  );
}
